import { useMemo } from "react";
import { useLocation } from "react-router";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import { ICategory } from "web/types/Category";
import { Nullable } from "web/types/Utils";

import getChildrenCategoriesToShow from "./getChildrenCategoriesToShow";
import getParentCategoryById from "./getParentCategoryById";
import getSiblingsCategoriesToShow from "./getSiblingsCategoriesToShow";

interface IUseCategoriesListArgs {
  parentCategoryId: Nullable<number>;
  categories: ICategory[];
  childrenIds: number[];
  siblingIds: number[];
  homePageData: Partial<ICategory>;
}

const useCategoriesList = ({
  parentCategoryId,
  categories,
  childrenIds,
  siblingIds,
  homePageData,
}: IUseCategoriesListArgs) => {
  const { search } = useLocation();
  const [parentCategory, categoriesToShow] = useMemo(() => {
    const parentCategoryById = getParentCategoryById(
      categories,
      parentCategoryId
    );
    const parentCategoryMemo = parentCategoryId
      ? {
          ...parentCategoryById,
          request_path: `${
            (parentCategoryById as ICategory).request_path
          }${search}`,
        }
      : homePageData;
    if (isArrayHasItems(categories)) {
      const categoriesChildrenToShow = getChildrenCategoriesToShow(
        categories,
        childrenIds
      );
      const categoriesSiblingsToShow = getSiblingsCategoriesToShow(
        categories,
        siblingIds
      );
      const categoriesToShowMemo = isArrayHasItems(categoriesChildrenToShow)
        ? categoriesChildrenToShow
        : categoriesSiblingsToShow;

      return [
        parentCategoryMemo,
        categoriesToShowMemo?.map((category) => ({
          ...category,
          request_path: `${category.request_path}${search}`,
        })),
      ];
    }

    return [parentCategoryMemo, []];
  }, [
    parentCategoryId,
    JSON.stringify(categories),
    siblingIds,
    childrenIds,
    search,
    homePageData,
  ]);

  return { parentCategory, categoriesToShow };
};

export default useCategoriesList;
